import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Mail,
  Globe,
} from 'lucide-react';

/**
 * AI Call Screening — employer-facing product page (lean showcase).
 *
 * Presents SearchJob.ai's AI voice-screening agent: it phones shortlisted
 * candidates and runs a roughly 15-minute screening, asking
 * role-specific questions taken straight from the job description — in
 * English — scoring every answer, recording the call and handing the
 * recruiter a live transcript in the dashboard, 24/7 with no recruiter
 * time. It pairs with WhatsApp screening, which captures the HR/logistics
 * answers (interest, current & expected CTC, notice period) as structured
 * data. Both are live today.
 *
 * Structure mirrors StaffingServices.jsx: a split hero (content left,
 * ImageCard right), two alternating FeatureRows, a dark stats band and a
 * dark final CTA. Rendered in the SearchJob.ai landing theme (cream base,
 * forest-green primary, gradient accents, rounded-3xl cards, dark-ink
 * bands). Helpers are defined locally.
 *
 * Route: /hirewithus/ai-call-screening
 */

const INK = 'hsl(164 33% 7%)';

export default function AICallScreening() {
  const navigate = useNavigate();

  useSEO({
    title:
      'AI Phone & Call Screening — AI Voice Calls on Role Questions | SearchJob.ai',
    description:
      'SearchJob.ai screens candidates with AI voice calls that ask role-specific questions from your job description, score each answer, and hand you a recorded transcript — plus WhatsApp screening that captures CTC, notice period and interest. 24/7, no recruiter time.',
    canonical: 'https://searchjob.ai/hirewithus/ai-call-screening',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  // Two alternating highlight rows — the call itself, then the result.
  const featured = [
    {
      eyebrow: 'The screening call',
      title: 'A ~15-minute screening call,',
      accent: 'on every shortlisted candidate.',
      body: 'The AI voice agent phones the candidate and runs a roughly 15-minute screening, working through the role-specific questions drawn straight from the job description — all in English, around the clock, with no recruiter time on the line.',
      points: [
        '~15-minute structured screening call',
        'Asks role questions from the JD',
        'Conducted in English',
        'Runs 24/7 — no recruiter time',
      ],
      img: '/call-questions-cut.png',
      alt: 'AI voice agent asking role-based screening questions on a call',
      flip: false,
    },
    {
      eyebrow: 'Scored transcript',
      title: 'A scored transcript,',
      accent: 'waiting in your dashboard.',
      body: 'Every answer is scored and flagged as the call happens, the full conversation is recorded and transcribed, and the result lands in your dashboard — while WhatsApp separately captures interest, current & expected CTC and notice period.',
      points: [
        'Every answer scored and flagged',
        'Full recording + live transcript',
        'Results delivered to your dashboard',
        'WhatsApp captures CTC, notice & interest',
      ],
      img: '/call-transcript-cut.png',
      alt: 'AI-generated call transcript with scored answers and a 15-minute timer',
      flip: true,
    },
  ];

  const stats = [
    { v: '~15 min', l: 'Per screening call' },
    { v: '24/7', l: 'Calls placed' },
    { v: '100%', l: 'Recorded & transcribed' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO (split) ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-16">
            <div className="max-w-4xl mx-auto text-center">
              <div>
                <Eyebrow>AI Call Screening</Eyebrow>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.06] mb-6">
                  AI that calls and screens
                  <br />
                  <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    every candidate.
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
                  AI voice calls run a roughly 15-minute screening in English,
                  asking role questions straight from your job description —
                  plus WhatsApp captures CTC, notice period and interest.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/signup')}
                    className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
                  >
                    Book a Demo
                  </Button>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <TrustDot text="~15-min screening calls" />
                  <TrustDot text="English-language" />
                  <TrustDot text="Recorded & transcribed" />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ═════════════ FEATURE ROWS ═════════════ */}
        <Section>
          <div className="space-y-16 lg:space-y-24">
            {featured.map((f) => (
              <FeatureRow key={f.eyebrow} {...f} />
            ))}
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark) ═════════════ */}
        <section
          className="relative py-14 lg:py-20 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                By the numbers
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Every shortlisted candidate,{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  actually screened.
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {stats.map((s) => (
                <div key={s.l}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {s.v}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ FINAL CTA (dark) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                    Let AI make the first call.
                    <br />
                    <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                      You take the best ones.
                    </span>
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed mb-8">
                    Point it at a role and a shortlist. Every candidate gets a
                    real screening call and a WhatsApp check — you get scored
                    transcripts to review, any time of day.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      onClick={() => navigate('/hirewithus/contact')}
                      className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                    >
                      Request a Demo
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/hirewithus/demo')}
                      className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                    >
                      Start Free Trial
                    </Button>
                  </div>
                </div>

                <div className="lg:pl-8 lg:border-l lg:border-white/10 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                    Talk to our team
                  </p>
                  <DarkContactLine icon={MapPin} text="Bangalore, India" />
                  <DarkContactLine icon={Mail} text="contact@searchjob.ai" />
                  <DarkContactLine icon={Globe} text="www.searchjob.ai" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Local building blocks — mirror the landing / StaffingServices    */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false, id }) {
  return (
    <section
      id={id}
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[480px] mx-auto h-auto drop-shadow-xl"
      />
    </div>
  );
}

function FeatureRow({ eyebrow, title, accent, body, points, img, alt, flip }) {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div className={flip ? 'lg:order-2' : ''}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <SectionHeading plain={title} accent={accent} />
        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
          {body}
        </p>
        <CheckList items={points} />
      </div>
      <ImageCard src={img} alt={alt} className={flip ? 'lg:order-1' : ''} />
    </div>
  );
}

function TrustDot({ text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-4 h-4 text-primary" />
      {text}
    </span>
  );
}

function DarkContactLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-emerald-300" />
      </span>
      <span className="text-sm font-medium text-white/90">{text}</span>
    </div>
  );
}
